/**
 * Wammy-style sustain drone scheduled by {@link AudioEngine} for the
 * duration of a Guitar hold. While a hold is engaged the corresponding
 * lane emits a low pentatonic sawtooth with a subtle 6 Hz pitch
 * wobble (~±20 cents) — the "string under your finger is bending"
 * cue an idle hold otherwise lacks.
 *
 * Cheap on purpose: one oscillator + one LFO oscillator + one
 * lowpass + two gain nodes per active drone. With four lanes that
 * caps at ~20 AudioNodes, which the stage bus compressor + saturator
 * + reverb chain still has plenty of headroom for.
 *
 * Stateful so the engine can stop / fade each drone individually —
 * factory functions returning object literals would obscure the
 * teardown contract and make sequencing the fade-out + node stop
 * error-prone.
 */
import { midiToHz } from './sfx';

/**
 * MIDI roots for each lane, mirroring a low bass-guitar tuning
 * (E1–A1–D2–G2). Sits a full octave below the chord stab so the
 * drone supplies "rumble under the riff" without fighting the
 * sparkle on top of the hit click.
 */
const LANE_ROOT_MIDI: ReadonlyArray<number> = [28, 33, 38, 43];

/** Drone peak gain. Subtle on purpose; this is presence, not melody. */
const DRONE_PEAK_GAIN = 0.085;

/** Lowpass cutoff (Hz). Cuts the sawtooth's brittle top so it reads as a bass drone. */
const FILTER_CUTOFF_HZ = 820;
const FILTER_Q = 2.2;

/** LFO speed (Hz) and depth (cents). 6 Hz ≈ classic guitar vibrato rate. */
const VIBRATO_RATE_HZ = 6;
const VIBRATO_DEPTH_CENTS = 22;

/** Attack and release ramps; short enough to start/stop a hold without click. */
const ATTACK_SECONDS = 0.05;
const RELEASE_SECONDS = 0.09;

export interface GuitarHoldDroneOptions {
  pan?: number;
}

/**
 * Lifecycle: `new GuitarHoldDrone(...)` immediately schedules the
 * fade-in; `stop()` schedules a fade-out followed by node teardown.
 * Once stopped the instance is dead — callers create a fresh one on
 * the next hold.
 */
export class GuitarHoldDrone {
  private ctx: AudioContext;
  private osc: OscillatorNode;
  private filter: BiquadFilterNode;
  private mainGain: GainNode;
  private lfo: OscillatorNode;
  private lfoGain: GainNode;
  private panner: StereoPannerNode | null;
  private stopped = false;
  private teardownTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    ctx: AudioContext,
    destination: AudioNode,
    lane: number,
    opts: GuitarHoldDroneOptions = {},
  ) {
    this.ctx = ctx;
    const laneIdx = Math.max(0, Math.min(LANE_ROOT_MIDI.length - 1, lane));
    const baseHz = midiToHz(LANE_ROOT_MIDI[laneIdx]);

    this.osc = ctx.createOscillator();
    this.osc.type = 'sawtooth';
    this.osc.frequency.value = baseHz;

    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = FILTER_CUTOFF_HZ;
    this.filter.Q.value = FILTER_Q;

    this.mainGain = ctx.createGain();
    this.mainGain.gain.value = 0;

    this.lfo = ctx.createOscillator();
    this.lfo.type = 'sine';
    this.lfo.frequency.value = VIBRATO_RATE_HZ;
    this.lfoGain = ctx.createGain();
    this.lfoGain.gain.value = VIBRATO_DEPTH_CENTS;
    this.lfo.connect(this.lfoGain).connect(this.osc.detune);

    const pan = opts.pan ?? 0;
    if (typeof ctx.createStereoPanner === 'function') {
      this.panner = ctx.createStereoPanner();
      this.panner.pan.value = Math.max(-1, Math.min(1, pan));
      this.osc.connect(this.filter).connect(this.mainGain).connect(this.panner).connect(destination);
    } else {
      this.panner = null;
      this.osc.connect(this.filter).connect(this.mainGain).connect(destination);
    }

    const now = ctx.currentTime;
    this.osc.start(now);
    this.lfo.start(now);
    this.mainGain.gain.setValueAtTime(0, now);
    this.mainGain.gain.linearRampToValueAtTime(DRONE_PEAK_GAIN, now + ATTACK_SECONDS);
  }

  /**
   * Fade out and tear down. Idempotent — subsequent calls are no-ops
   * so a double-stop (e.g. session teardown overlapping a hold
   * release) doesn't double-schedule the release ramp.
   */
  stop(): void {
    if (this.stopped) {
      return;
    }
    this.stopped = true;
    const now = this.ctx.currentTime;
    try {
      const gain = this.mainGain.gain;
      const startValue = Math.max(gain.value, 0.0001);
      gain.cancelScheduledValues(now);
      gain.setValueAtTime(startValue, now);
      gain.linearRampToValueAtTime(0, now + RELEASE_SECONDS);
    } catch (err) {
      console.warn('GuitarHoldDrone.stop: gain ramp failed:', err);
    }
    const teardownAt = (RELEASE_SECONDS + 0.04) * 1000;
    this.teardownTimer = setTimeout(() => {
      this.teardownTimer = null;
      this.teardown();
    }, teardownAt);
  }

  /**
   * Hard cut + immediate teardown. Used by {@link AudioEngine.stop}
   * during session destruction so the audio context can close
   * without waiting on the release tail.
   */
  destroy(): void {
    if (this.teardownTimer !== null) {
      clearTimeout(this.teardownTimer);
      this.teardownTimer = null;
    }
    this.stopped = true;
    this.teardown();
  }

  private teardown(): void {
    try {
      this.osc.stop();
    } catch {
      // Already stopped (e.g. context closed) — nothing to do.
    }
    try {
      this.lfo.stop();
    } catch {
      // Already stopped — nothing to do.
    }
    for (const node of [this.osc, this.lfo, this.lfoGain, this.filter, this.mainGain, this.panner]) {
      if (!node) {
        continue;
      }
      try {
        node.disconnect();
      } catch (err) {
        console.warn('GuitarHoldDrone.teardown: disconnect failed:', err);
      }
    }
  }
}
